import {gql} from '@apollo/client';
import {Color} from '@benchling/nucleus-ui';
import {
  ActionIcon,
  Box,
  Group,
  Menu,
  Paper,
  Stack,
  Switch,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useRef, useState} from 'react';

import {assertNotNullAndReturn} from 'Asserts';
import * as NavConstants from 'constants/NavConstants';
import {
  CachedDataWhenRefetchingNamedOption,
  QueryResultType,
  useBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import navigateToURL from 'lib/navigateToURL';
import {MCPToolMappingProvider} from 'llm/mcp-client-library/MCPToolMappingContext';
import {MCP_PRODUCT_NAME_PLURAL} from 'llm/NamingConstants';
import {RootCrumb} from 'nav/Breadcrumbs';
import ListingHeader from 'nav/sidepanel/header/ListingHeader';
import ToggleDropdown from 'nav/ToggleDropdown';
import notifications from 'notifications';
import EntityStore from 'stores/EntityStore';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as nyc from 'ui-library/NYC';
import {deletePromise, isServerRequestError, putJSONPromise} from 'util/Net';

import {MCPSidepanelQuery} from './__generated/MCPSidepanelQuery';
import AddMCPServerModal from './AddMCPServerModal';
import AdminMcpConfigurationModal from './AdminMcpConfigurationModal';
import ConnectMCPModal from './ConnectMCPModal';
import {useMCPPing} from './useMCPPing';
import UserToolPermissionsModal from './UserMcpConfigurationModal';

// Mantine Switch styles (require objects, not Aphrodite classes)
const switchTrackEnabledStyle = {backgroundColor: Color.BLUE_500, borderColor: Color.BLUE_500};
const switchTrackDisabledStyle = {backgroundColor: Color.GRAY_500, borderColor: Color.GRAY_500};

export const MCP_SIDEPANEL_QUERY = gql`
  query MCPSidepanelQuery @skipAudit @engComponent(name: LLM) {
    mcpServerConfigs {
      id
      name
      description
      extensionDefinitionId
    }
    mcpServerUserConfigs {
      id
      isEnabled
      mcpServerConfig {
        id
        name
        description
        extensionDefinitionId
      }
    }
  }
`;

interface MCPSidepanelProps {
  isExpanded: boolean;
  isCompactDisplay: boolean;
  setIsCompactDisplay: (isCompact: boolean) => void;
  activeItemId: string | null;
  connectionsTab: JSX.Element;
}

type MCPServerConfig = MCPSidepanelQuery['mcpServerConfigs'][number];
type MCPServerUserConfig = MCPSidepanelQuery['mcpServerUserConfigs'][number];

interface ConnectedCardProps {
  config: MCPServerUserConfig;
  onToggleEnabled: (config: MCPServerUserConfig) => void;
  onDisconnect: (config: MCPServerUserConfig) => void;
  onManageUserTools: (config: MCPServerUserConfig) => void;
  onManageAdminTools: (configOrId: MCPServerConfig | string) => void;
  isAdmin: boolean;
}

interface AvailableCardProps {
  config: MCPServerConfig;
  onConnect: (config: MCPServerConfig) => void;
  onManageTools: (config: MCPServerConfig) => void;
  onDelete: (config: MCPServerConfig) => void;
  isAdmin: boolean;
}

function ConnectedCard(props: ConnectedCardProps): JSX.Element {
  const {config, onToggleEnabled, onDisconnect, onManageUserTools, onManageAdminTools, isAdmin} = props;
  const {isEnabled} = config;
  const {id, name, description} = config.mcpServerConfig;
  const switchTrackStyle = isEnabled ? switchTrackEnabledStyle : switchTrackDisabledStyle;

  // Poll for connection status every 5 minutes when enabled
  const {
    status: pingStatus,
    isLoading: isPinging,
    refetch: refetchPingStatus,
  } = useMCPPing({
    serverId: id,
    enabled: isEnabled,
    pollingInterval: 5 * 60 * 1000, // 5 minutes
  });

  // Determine the status text and style based on ping result
  let statusText = 'DISABLED';
  let statusStyle = styles.statusBadge;
  let isAuthRequired = false;
  let statusTooltip: string | undefined;

  if (isEnabled) {
    if (isPinging && !pingStatus) {
      statusText = 'CHECKING...';
      statusStyle = styles.statusBadgeChecking;
    } else if (pingStatus?.isConnected) {
      statusText = 'CONNECTED';
      statusStyle = styles.statusBadgeConnected;
    } else if (pingStatus?.needsRedirect) {
      statusText = 'AUTH REQUIRED';
      statusStyle = styles.statusBadgeAuthRequired;
      isAuthRequired = true;
    } else {
      statusText = 'DISCONNECTED';
      statusStyle = styles.statusBadgeDisconnected;
      statusTooltip = pingStatus?.errorMessage ?? undefined;
    }
  }

  const handleAuthClick = (): void => {
    if (pingStatus?.redirectUrl) {
      const authWindow = window.open(pingStatus.redirectUrl, '_blank');

      // Poll to check if the window is closed
      if (authWindow) {
        const pollInterval = setInterval(() => {
          if (authWindow.closed) {
            clearInterval(pollInterval);
            // Re-check auth status after window closes
            refetchPingStatus();
          }
        }, 500); // Check every 500ms
      }
    }
  };

  return (
    <Paper className={css(styles.card, !isEnabled && styles.cardDisabled)} p={0} radius={4} withBorder={true}>
      <Group noWrap={true} position="apart" align="flex-start" className={css(styles.cardInner)}>
        <Group noWrap={true} spacing={8} align="flex-start" style={{minWidth: 0, flex: 1}}>
          <Box className={css(styles.mcpIcon, !isEnabled && styles.mcpIconDisabled)}>
            <BenchlingIcon icon="sources" />
          </Box>
          <Stack spacing={8} style={{minWidth: 0, width: 173}}>
            <Stack spacing={2}>
              <Text className={css(styles.cardTitle)} lineClamp={1}>
                {name}
              </Text>
              {description && (
                <Text className={css(styles.cardMetaText)} lineClamp={1}>
                  {description}
                </Text>
              )}
            </Stack>
            <Tooltip label={statusTooltip} disabled={!statusTooltip} withinPortal={true}>
              {isAuthRequired ? (
                <UnstyledButton
                  className={css(styles.statusBadge, statusStyle, styles.statusBadgeClickable)}
                  onClick={handleAuthClick}
                  aria-description="Click to re-authenticate"
                >
                  {statusText}
                </UnstyledButton>
              ) : (
                <div className={css(styles.statusBadge, statusStyle)}>{statusText}</div>
              )}
            </Tooltip>
          </Stack>
        </Group>
        <Group noWrap={true} spacing={4} align="center" className={css(styles.cardActions)}>
          <Switch
            checked={isEnabled}
            onChange={() => {
              onToggleEnabled(config);
            }}
            size="sm"
            styles={{track: switchTrackStyle}}
            aria-label={isEnabled ? 'Disable connector' : 'Enable connector'}
          />
          <Menu position="bottom-end" withinPortal={true}>
            <Menu.Target>
              <ActionIcon
                variant="subtle"
                size="sm"
                className={css(styles.menuButton)}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                }}
              >
                <BenchlingIcon icon="ellipsis-vertical" />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                onClick={() => {
                  onManageUserTools(config);
                }}
              >
                Configuration
              </Menu.Item>
              {isAdmin && (
                <Menu.Item
                  onClick={() => {
                    onManageAdminTools(id);
                  }}
                >
                  Admin Settings
                </Menu.Item>
              )}
              <Menu.Item
                color="red"
                onClick={() => {
                  onDisconnect(config);
                }}
              >
                Disconnect
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Group>
    </Paper>
  );
}

function AvailableCard(props: AvailableCardProps): JSX.Element {
  const {config, onConnect, onManageTools, onDelete, isAdmin} = props;
  const {name, description, extensionDefinitionId} = config;
  const isInstalledFromStore = extensionDefinitionId != null;

  return (
    <Paper className={css(styles.card)} p={0} radius={4} withBorder={true}>
      <Group noWrap={true} position="apart" align="flex-start" className={css(styles.cardInner)}>
        <Group noWrap={true} spacing={8} align="flex-start" style={{minWidth: 0, flex: 1}}>
          <Box className={css(styles.mcpIcon)}>
            <BenchlingIcon icon="sources" />
          </Box>
          <Stack spacing={8} style={{minWidth: 0, width: 173}}>
            <Stack spacing={2}>
              <Text className={css(styles.cardTitle)} lineClamp={1}>
                {name}
              </Text>
              {description && (
                <Text className={css(styles.cardMetaText)} lineClamp={1}>
                  {description}
                </Text>
              )}
            </Stack>
          </Stack>
        </Group>
        <Group noWrap={true} spacing={4} align="center" className={css(styles.cardActions)}>
          <button
            className={css(styles.connectLink)}
            onClick={() => {
              onConnect(config);
            }}
            type="button"
          >
            Connect
          </button>
          {isAdmin && (
            <Menu position="bottom-end" withinPortal={true}>
              <Menu.Target>
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  className={css(styles.menuButton)}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                  }}
                >
                  <BenchlingIcon icon="ellipsis-vertical" />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  onClick={() => {
                    onManageTools(config);
                  }}
                >
                  Admin Settings
                </Menu.Item>
                {!isInstalledFromStore && (
                  <Menu.Item
                    color="red"
                    onClick={() => {
                      onDelete(config);
                    }}
                  >
                    Delete
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          )}
        </Group>
      </Group>
    </Paper>
  );
}

function MCPSidepanelHeader(props: MCPSidepanelProps & {onAddClick: () => void}): JSX.Element {
  const user = EntityStore.currentUser();
  return (
    <ListingHeader
      buttons={
        <>
          <ToggleDropdown className="u-mini-spacing-right" expanded={props.isExpanded} />
          <Button
            buttonStyle={ButtonStyle.TERTIARY_BLUE}
            onClick={props.onAddClick}
            size={ButtonSize.SMALL}
            disabled={!user || !user.isTenantAdmin}
            icon="plus"
            className="u-mini-spacing-right"
          />
        </>
      }
      crumbs={<RootCrumb text={MCP_PRODUCT_NAME_PLURAL} />}
      isExpanded={props.isExpanded}
      queryInput={<div />}
      filtersContainer={<div />}
      sectionTabs={props.connectionsTab}
    />
  );
}

interface MCPSidepanelContentsProps extends MCPSidepanelProps {
  connectedConfigs: Array<MCPServerUserConfig>;
  availableConfigs: Array<MCPServerConfig>;
  isLoading: boolean;
  isAdmin: boolean;
  onAddClick: () => void;
  onConnect: (config: MCPServerConfig) => void;
  onToggleEnabled: (config: MCPServerUserConfig) => void;
  onDisconnect: (config: MCPServerUserConfig) => void;
  onManageUserTools: (config: MCPServerUserConfig) => void;
  onManageAdminTools: (configOrId: MCPServerConfig | string) => void;
  onDelete: (config: MCPServerConfig) => void;
}

function CollapsedMCPSidepanelContents(props: MCPSidepanelContentsProps): JSX.Element {
  const {
    connectedConfigs,
    availableConfigs,
    onConnect,
    onToggleEnabled,
    onDisconnect,
    onManageUserTools,
    onManageAdminTools,
    onDelete,
    isAdmin,
  } = props;
  const isEmpty = connectedConfigs.length === 0 && availableConfigs.length === 0;

  return (
    <>
      <nyc.Row>
        <MCPSidepanelHeader {...props} />
      </nyc.Row>
      <nyc.Row
        aria-labelledby={`${NavConstants.Section.MCPS}_TAB`}
        className="u-vertical-flex"
        id={`${NavConstants.Section.MCPS}_PANEL`}
        fluid={true}
        role="tabpanel"
      >
        <div className="u-flex-grow u-position-relative">
          <div className="u-absolute-scroll-container">
            {props.isLoading ? (
              <CenteredHorizontalThrobber />
            ) : (
              <div className={css(styles.cardContainer)}>
                {isEmpty ? (
                  <div className="sidepanel-fadedMessage">
                    Add {MCP_PRODUCT_NAME_PLURAL} to reference other tools and services
                  </div>
                ) : (
                  <GroupedMCPConfigs
                    connectedConfigs={connectedConfigs}
                    availableConfigs={availableConfigs}
                    isAdmin={isAdmin}
                    onConnect={onConnect}
                    onToggleEnabled={onToggleEnabled}
                    onDisconnect={onDisconnect}
                    onManageUserTools={onManageUserTools}
                    onManageAdminTools={onManageAdminTools}
                    onDelete={onDelete}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </nyc.Row>
    </>
  );
}

function ExpandedMCPSidepanelContents(props: MCPSidepanelContentsProps): JSX.Element {
  const {
    connectedConfigs,
    availableConfigs,
    onConnect,
    onToggleEnabled,
    onDisconnect,
    onManageUserTools,
    onManageAdminTools,
    onDelete,
    isAdmin,
  } = props;
  const isEmpty = connectedConfigs.length === 0 && availableConfigs.length === 0;

  return (
    <nyc.Row fluid={true}>
      <nyc.Col lg={1} />
      <nyc.Col lg={20} className="u-flex-display u-vertical-flex">
        <MCPSidepanelHeader {...props} />
        <div
          aria-labelledby={NavConstants.Section.MCPS}
          className="u-flex-grow u-flex-display u-small-spacing-top"
          role="tabpanel"
        >
          {props.isLoading ? (
            <CenteredHorizontalThrobber />
          ) : (
            <div className={css(styles.expandedContainer)}>
              {isEmpty ? (
                <div className="sidepanel-fadedMessage">
                  Add {MCP_PRODUCT_NAME_PLURAL} to reference other tools and services for more context in AI
                  Experiences
                </div>
              ) : (
                <GroupedMCPConfigs
                  connectedConfigs={connectedConfigs}
                  availableConfigs={availableConfigs}
                  isAdmin={isAdmin}
                  onConnect={onConnect}
                  onToggleEnabled={onToggleEnabled}
                  onDisconnect={onDisconnect}
                  onManageUserTools={onManageUserTools}
                  onManageAdminTools={onManageAdminTools}
                  onDelete={onDelete}
                />
              )}
            </div>
          )}
        </div>
      </nyc.Col>
      <nyc.Col lg={1} />
    </nyc.Row>
  );
}

interface GroupedMCPConfigsProps {
  connectedConfigs: Array<MCPServerUserConfig>;
  availableConfigs: Array<MCPServerConfig>;
  isAdmin: boolean;
  onConnect: (config: MCPServerConfig) => void;
  onToggleEnabled: (config: MCPServerUserConfig) => void;
  onDisconnect: (config: MCPServerUserConfig) => void;
  onManageUserTools: (config: MCPServerUserConfig) => void;
  onManageAdminTools: (configOrId: MCPServerConfig | string) => void;
  onDelete: (config: MCPServerConfig) => void;
}

/** Renders MCP configs grouped by connection state with a divider between sections */
function GroupedMCPConfigs(props: GroupedMCPConfigsProps): JSX.Element {
  const {
    connectedConfigs,
    availableConfigs,
    onConnect,
    onToggleEnabled,
    onDisconnect,
    onManageUserTools,
    onManageAdminTools,
    onDelete,
    isAdmin,
  } = props;

  return (
    <>
      {connectedConfigs.length > 0 && (
        <div className={css(styles.section)}>
          <Text className={css(styles.sectionHeader)}>Connected</Text>
          <div className={css(styles.cardsGrid)}>
            {connectedConfigs.map((config) => (
              <ConnectedCard
                key={config.id}
                config={config}
                onToggleEnabled={onToggleEnabled}
                onDisconnect={onDisconnect}
                onManageUserTools={onManageUserTools}
                onManageAdminTools={onManageAdminTools}
                isAdmin={isAdmin}
              />
            ))}
          </div>
        </div>
      )}
      {connectedConfigs.length > 0 && availableConfigs.length > 0 && <div className={css(styles.divider)} />}
      {availableConfigs.length > 0 && (
        <div className={css(styles.section)}>
          <Text className={css(styles.sectionHeader)}>Available</Text>
          <div className={css(styles.cardsGrid)}>
            {availableConfigs.map((config) => (
              <AvailableCard
                key={config.id}
                config={config}
                onConnect={onConnect}
                onManageTools={onManageAdminTools}
                onDelete={onDelete}
                isAdmin={isAdmin}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
}

export default function MCPSidepanel(props: MCPSidepanelProps): JSX.Element {
  const [showAddModal, setShowAddModal] = useState(false);
  const [connectConfig, setConnectConfig] = useState<MCPServerConfig | null>(null);
  const [adminToolsConfig, setAdminToolsConfig] = useState<MCPServerConfig | null>(null);
  const [userToolsConfig, setUserToolsConfig] = useState<MCPServerUserConfig | null>(null);
  const queryResult = useBenchlingQuery<MCPSidepanelQuery>(MCP_SIDEPANEL_QUERY, {
    // Keep showing the connector list while refetching (e.g. after adding a server) so ConnectedCard
    // stays mounted and useMCPPing does not reset every MCP to "checking" / pending.
    renderCachedDataWhenRefetching: CachedDataWhenRefetchingNamedOption.WHEN_LOADING,
  });
  const prevExpandedRef = useRef(props.isExpanded);
  const user = EntityStore.currentUser();
  const isAdmin = Boolean(user?.isTenantAdmin);

  // Navigate to /ai-connectors when expanded
  useEffect(() => {
    if (props.isExpanded) {
      navigateToURL('/ai-connectors', {
        skipTrigger: true,
        replace: prevExpandedRef.current === props.isExpanded,
      });
    }
    prevExpandedRef.current = props.isExpanded;
  }, [props.isExpanded]);

  const isLoading = queryResult.type === QueryResultType.PENDING;
  const isErrored = queryResult.type === QueryResultType.ERROR;
  let connectedConfigs: Array<MCPServerUserConfig> = [];
  let availableConfigs: Array<MCPServerConfig> = [];
  let mcpServerConfigs: Array<MCPServerConfig> = [];

  if (isErrored) {
    notifications.error(`Could not load ${MCP_PRODUCT_NAME_PLURAL}; try reloading the page.`);
  } else if (queryResult.type === QueryResultType.SUCCESS) {
    mcpServerConfigs = queryResult.data.mcpServerConfigs;
    connectedConfigs = queryResult.data.mcpServerUserConfigs;
    const connectedServerIds = new Set(connectedConfigs.map((uc) => uc.mcpServerConfig.id));
    availableConfigs = mcpServerConfigs.filter((sc) => !connectedServerIds.has(sc.id));
  }

  const handleAddClick = (): void => {
    setShowAddModal(true);
  };
  const handleAddModalClose = (): void => {
    setShowAddModal(false);
  };
  const handleAddModalSubmit = (): void => {
    setShowAddModal(false);
    queryResult.refetch();
  };

  const handleConnect = (config: MCPServerConfig): void => {
    setConnectConfig(config);
  };
  const handleConnectModalClose = (): void => {
    setConnectConfig(null);
  };
  const handleConnectModalSubmit = (userConfigId: string): void => {
    const serverConfig = connectConfig;
    setConnectConfig(null);
    queryResult.refetch();

    // After connecting, open the appropriate settings modal
    if (serverConfig) {
      if (isAdmin) {
        // Admin users go to admin settings modal
        setAdminToolsConfig(serverConfig);
      } else {
        // Regular users go to user tool permissions modal
        setUserToolsConfig({
          __typename: 'McpServerUserConfigNode',
          id: userConfigId,
          isEnabled: true,
          mcpServerConfig: serverConfig,
        });
      }
    }
  };

  const handleToggleEnabled = async (config: MCPServerUserConfig): Promise<void> => {
    const {name} = config.mcpServerConfig;
    try {
      await putJSONPromise({
        url: `/1/api/llm/mcp-server-user-configs/${config.id}`,
        data: {isEnabled: !config.isEnabled},
      });
      notifications.success(`${name} ${config.isEnabled ? 'disabled' : 'enabled'} successfully`);
      queryResult.refetch();
    } catch (e) {
      if (isServerRequestError(e)) {
        notifications.error(`Could not ${config.isEnabled ? 'disable' : 'enable'} ${name}`);
      } else {
        throw e;
      }
    }
  };

  const handleDisconnect = async (config: MCPServerUserConfig): Promise<void> => {
    try {
      await deletePromise({
        url: `/1/api/llm/mcp-server-user-configs/${config.id}`,
      });
      notifications.success(`${config.mcpServerConfig.name} disconnected successfully`);
      queryResult.refetch();
    } catch (e) {
      if (isServerRequestError(e)) {
        notifications.error(`Could not disconnect ${config.mcpServerConfig.name}`);
      } else {
        throw e;
      }
    }
  };

  const handleManageAdminTools = (configOrId: MCPServerConfig | string): void => {
    if (typeof configOrId === 'string') {
      const configOrUndefined = mcpServerConfigs.find((c) => c.id === configOrId);
      const config = assertNotNullAndReturn(configOrUndefined);
      setAdminToolsConfig(config);
    } else {
      setAdminToolsConfig(configOrId);
    }
  };
  const handleAdminToolsModalClose = (): void => {
    setAdminToolsConfig(null);
  };
  const handleAdminToolsModalSubmit = (): void => {
    setAdminToolsConfig(null);
    queryResult.refetch();
  };
  const handleAdminToolsModalSave = (): void => {
    queryResult.refetch();
  };

  const handleManageUserTools = (config: MCPServerUserConfig): void => {
    setUserToolsConfig(config);
  };
  const handleUserToolsModalClose = (): void => {
    setUserToolsConfig(null);
  };
  const handleUserToolsModalSubmit = (): void => {
    setUserToolsConfig(null);
    queryResult.refetch();
  };

  const handleDelete = async (config: MCPServerConfig): Promise<void> => {
    try {
      await deletePromise({
        url: `/1/api/llm/mcp-server-configs/${config.id}`,
      });
      notifications.success(`${config.name} deleted successfully`);
      queryResult.refetch();
    } catch (e) {
      if (isServerRequestError(e)) {
        notifications.error(`Could not delete ${config.name}`);
      } else {
        throw e;
      }
    }
  };

  return (
    <>
      {showAddModal && (
        <AddMCPServerModal onRequestClose={handleAddModalClose} onRequestSubmit={handleAddModalSubmit} />
      )}
      {connectConfig && (
        <ConnectMCPModal
          config={connectConfig}
          onRequestClose={handleConnectModalClose}
          onRequestSubmit={handleConnectModalSubmit}
        />
      )}
      {adminToolsConfig && (
        <MCPToolMappingProvider>
          <AdminMcpConfigurationModal
            serverId={adminToolsConfig.id}
            serverName={adminToolsConfig.name}
            isInstalledFromStore={adminToolsConfig.extensionDefinitionId != null}
            isAdmin={isAdmin}
            onRequestClose={handleAdminToolsModalClose}
            onRequestSubmit={handleAdminToolsModalSubmit}
            onSave={handleAdminToolsModalSave}
          />
        </MCPToolMappingProvider>
      )}
      {userToolsConfig && (
        <MCPToolMappingProvider>
          <UserToolPermissionsModal
            userConfigId={userToolsConfig.id}
            serverId={userToolsConfig.mcpServerConfig.id}
            serverName={userToolsConfig.mcpServerConfig.name}
            onRequestClose={handleUserToolsModalClose}
            onRequestSubmit={handleUserToolsModalSubmit}
          />
        </MCPToolMappingProvider>
      )}
      <nyc.Container
        className={classNames('sidepanel', {expandedContainer: props.isExpanded})}
        size={props.isExpanded ? 'lg' : 'sidepanel'}
      >
        {props.isExpanded ? (
          <ExpandedMCPSidepanelContents
            {...props}
            connectedConfigs={connectedConfigs}
            availableConfigs={availableConfigs}
            isLoading={isLoading}
            isAdmin={isAdmin}
            onAddClick={handleAddClick}
            onConnect={handleConnect}
            onToggleEnabled={handleToggleEnabled}
            onDisconnect={handleDisconnect}
            onManageUserTools={handleManageUserTools}
            onManageAdminTools={handleManageAdminTools}
            onDelete={handleDelete}
          />
        ) : (
          <CollapsedMCPSidepanelContents
            {...props}
            connectedConfigs={connectedConfigs}
            availableConfigs={availableConfigs}
            isLoading={isLoading}
            isAdmin={isAdmin}
            onAddClick={handleAddClick}
            onConnect={handleConnect}
            onToggleEnabled={handleToggleEnabled}
            onDisconnect={handleDisconnect}
            onManageUserTools={handleManageUserTools}
            onManageAdminTools={handleManageAdminTools}
            onDelete={handleDelete}
          />
        )}
      </nyc.Container>
    </>
  );
}

const styles = StyleSheet.create({
  card: {
    cursor: 'pointer',
    width: 350,
    height: 80,
    overflow: 'hidden',
    transition: 'box-shadow 0.15s ease-in-out, border-color 0.15s ease-in-out',
    backgroundColor: Color.WHITE,
    borderColor: Color.GRAY_200,
    ':hover': {
      boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.08)',
      borderColor: Color.GRAY_300,
    },
  },
  cardDisabled: {
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    borderColor: 'rgba(226, 227, 229, 0.8)',
  },
  cardInner: {
    padding: '16px 16px 8px 16px',
    height: '100%',
  },
  cardTitle: {
    color: Color.GRAY_900,
    fontSize: 13,
    fontWeight: 600,
    lineHeight: '16px',
  },
  cardMetaText: {
    color: Color.GRAY_700,
    fontSize: 12,
    fontWeight: 400,
    lineHeight: '14px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  mcpIcon: {
    width: 30,
    height: 30,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 3,
    border: `1px solid ${Color.GRAY_200}`,
  },
  mcpIconDisabled: {
    opacity: 0.8,
    borderColor: 'rgba(226, 227, 229, 0.8)',
  },
  statusBadge: {
    width: 'fit-content',
    textTransform: 'uppercase',
    fontSize: 10,
    fontWeight: 600,
    lineHeight: '12px',
    backgroundColor: Color.GRAY_100,
    color: Color.GRAY_800,
    padding: '2px 6px',
    borderRadius: 4,
  },
  statusBadgeConnected: {
    backgroundColor: Color.GREEN_200,
    color: Color.GREEN_600,
  },
  statusBadgeDisconnected: {
    backgroundColor: Color.RED_150,
    color: Color.RED_600,
  },
  statusBadgeChecking: {
    backgroundColor: Color.YELLOW_200,
    color: Color.YELLOW_600,
  },
  statusBadgeAuthRequired: {
    backgroundColor: Color.YELLOW_200,
    color: Color.YELLOW_600,
  },
  statusBadgeClickable: {
    cursor: 'pointer',
    ':hover': {
      backgroundColor: Color.YELLOW_250,
      textDecoration: 'underline',
    },
  },
  connectLink: {
    color: Color.BLUE_500,
    fontSize: 13,
    fontWeight: 600,
    lineHeight: '16px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px 8px',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  menuButton: {
    color: Color.BLUE_500,
    padding: '4px 8px',
  },
  cardActions: {
    flexShrink: 0,
  },
  cardContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    padding: 12,
    width: '100%',
  },
  expandedContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    width: '100%',
    padding: 12,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  sectionHeader: {
    fontSize: 12,
    fontWeight: 600,
    color: Color.GRAY_600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 4,
  },
  cardsGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    alignContent: 'flex-start',
    gap: '12px 16px',
  },
  divider: {
    width: '100%',
    height: 1,
    backgroundColor: Color.GRAY_200,
    margin: '20px 0',
  },
});
